/**
 * Session-wipe reasons.
 *
 * Every path that removes the Entra session bytes (or resets all session
 * state) must say WHY. Until Sep 2026 the wipe sites were indistinguishable
 * after the fact: localStorage is empty on every door, and because the key
 * is shared across tabs, a wipe in one tab surfaces in every other tab as
 * "[auth] Entra session expired in background" — so console archaeology
 * after a logout could never tell a rejected refresh from a boot-watchdog
 * reset in a tab that was already gone. The reason travels three ways:
 *
 *   - /login?reason=<code>   (visible to the user / in a screenshot)
 *   - App Insights `session_wipe` event (queryable in prod)
 *   - PostHog `session_wipe` event (per-user funnels)
 */
import { trackEvent as aiTrackEvent } from '@/services/telemetryService'

export type SessionWipeReason =
  // Boot watchdog overlay: the user chose "Reset session" on a stuck boot.
  | 'boot_timeout'
  // LoginView "Reset session" link, or a `?reset=1` bookmark boot.
  | 'manual_reset'
  // auth.init(): the refresh probe timed out twice in a row (while online).
  | 'probe_timeout_x2'
  // /api/auth/entra/refresh answered 400/401 — Entra rejected the refresh_token.
  | 'refresh_rejected'
  // The stored session had no refresh_token to recover with.
  | 'no_refresh_token'
  // /api/auth/db-jwt answered 401 twice, the second time with a freshly
  // refreshed id_token.
  | 'db_jwt_401x2'
  // 403 PENDING_ROLE_ASSIGNMENT — first login, no role yet.
  | 'pending_role'
  // SuperTokens SDK reported the session expired / revoked.
  | 'st_unauthorised'
  // The watchdog found the key gone while this tab still believed it was
  // signed in — another tab (or DevTools) removed it.
  | 'wiped_elsewhere'
  // Explicit sign-out from the user menu.
  | 'user_signout'
  // LoginView "sign in with a different account".
  | 'different_account'
  // LoginView starts (or fails) a fresh OAuth dance over stale bytes.
  | 'login_restart'

/** Reasons that deserve the amber "your session ended" banner on /login. */
export const BANNER_REASONS: ReadonlySet<string> = new Set<SessionWipeReason>([
  'boot_timeout',
  'probe_timeout_x2',
  'refresh_rejected',
  'no_refresh_token',
  'db_jwt_401x2',
  'st_unauthorised',
  'wiped_elsewhere',
])

export interface SessionWipeContext {
  /** ms between now and the stored expires_at (negative = already past). */
  ms_to_expiry?: number | null
  had_refresh_token?: boolean
  /** HTTP status that triggered the wipe, when there was one. */
  status?: number
  [k: string]: string | number | boolean | null | undefined
}

/**
 * Log + ship one wipe. Never throws — a telemetry hiccup must not block a
 * logout path. The console line is a `warn` so it survives the console gate.
 */
export function reportSessionWipe(reason: SessionWipeReason, context: SessionWipeContext = {}): void {
  const props: Record<string, string> = { reason }
  try {
    props.online = String(typeof navigator !== 'undefined' ? navigator.onLine : 'unknown')
    props.visibility = typeof document !== 'undefined' ? document.visibilityState : 'unknown'
    props.path = typeof window !== 'undefined' ? window.location.pathname : ''
    for (const [k, v] of Object.entries(context)) {
      if (v !== undefined && v !== null) props[k] = String(v)
    }
  } catch { /* context is best-effort */ }

  console.warn(`[session] wiped — reason=${reason}`, props)
  try { aiTrackEvent('session_wipe', props) } catch { /* never block on telemetry */ }
  // PostHog is loaded lazily: importing it statically here would pull
  // posthog-js into every module that can wipe a session (entraAuthService,
  // sessionReset, …) and their unit tests.
  void import('@/composables/usePostHog')
    .then(({ usePostHog }) => { usePostHog().trackEvent('session_wipe', props) })
    .catch(() => { /* telemetry is optional */ })
}
